import { useCallback, useState } from 'react';
import { Form, Formik } from 'formik';
import type { FormikHelpers } from 'formik';

import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import { EMPTY_OBJECT, EMPTY_STRING } from '@e-socna/empty';

import { useUiContext } from '../../contexts/UiContext/hooks/useUiContext.hook';
import { useDialogAutoFocus } from '../../hooks/useDialogAutoFocus.hook';

import { MemoBeforeSubmitForm } from '../MemoBeforeSubmit';
import { useMemos } from '../MemoBeforeSubmit/hooks/useMemos.hook';
import { AlertMessages } from '../../components/AlertMessages';

import type { SubmitHandler } from './types/submit-handler.type';

interface Props<V extends object> {
  readonly askMemoBeforeSubmit?: boolean;
  readonly isDialogOpen: boolean;
  readonly onCloseDialog: () => void;
  readonly onSubmit: SubmitHandler<V>;
  readonly useDirectMutation: boolean;
}

export const DeleteResolvableServiceUsageForm = <V extends object>({
  askMemoBeforeSubmit = true,
  isDialogOpen,
  onCloseDialog: handleCloseDialog,
  onSubmit: pHandleSubmit,
  useDirectMutation,
}: Readonly<Props<V>>) => {
  const { ui } = useUiContext();

  const dialogAutoFocusSlotProps = useDialogAutoFocus();

  // on error
  const [formError, setFormError] = useState<readonly string[] | null>(null);

  const { clearMemos, memos, setMemos } = useMemos();

  const handleSubmit = useCallback(
    (values: V, formikHelpers: FormikHelpers<V>) => {
      void pHandleSubmit({ values, memos, formikHelpers, setFormError })?.then(
        clearMemos,
      );
    },
    [clearMemos, pHandleSubmit, memos],
  );

  return (
    <Formik
      enableReinitialize
      initialValues={EMPTY_OBJECT as V}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: formikSubmit, isSubmitting, submitForm, isValid }) => {
        const isLoading = isSubmitting || ui.isBackgroundOperationRunning;

        const submitLabel = 'Supprimer';

        return (
          <Form onSubmit={formikSubmit}>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-type-conversion */}
            {!!askMemoBeforeSubmit && (
              <MemoBeforeSubmitForm
                clearMemos={clearMemos}
                isLoading={isLoading}
                memos={memos}
                onConfirm={submitForm}
                setMemos={setMemos}
                submitLabel={submitLabel}
              />
            )}
            <Dialog
              aria-describedby="delete-resolvable-service-usage-dialog-description"
              aria-labelledby="delete-resolvable-service-usage-dialog-title"
              onClose={handleCloseDialog}
              open={isDialogOpen}
              slotProps={dialogAutoFocusSlotProps}
            >
              <DialogTitle id="delete-resolvable-service-usage-dialog-title">
                Confirmation de la suppression de la prestation
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="delete-resolvable-service-usage-dialog-description">
                  Êtes-vous sûr de vouloir supprimer cette prestation ?
                  {useDirectMutation
                    ? ' Cette action est définitive.'
                    : EMPTY_STRING}
                </DialogContentText>
                {!!formError && (
                  <AlertMessages messages={formError} severity="error" />
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog} size="large">
                  Annuler
                </Button>
                <Button
                  color="warning"
                  data-dialog-autofocus
                  disabled={!isValid}
                  loading={isLoading}
                  // eslint-disable-next-line react/jsx-no-bind
                  onClick={
                    askMemoBeforeSubmit
                      ? () => {
                          setMemos((oldMemos) => ({
                            ...oldMemos,
                            isOpenMemoDialog: true,
                          }));
                        }
                      : submitForm
                  }
                  size="large"
                  variant="contained"
                >
                  {submitLabel}
                </Button>
              </DialogActions>
            </Dialog>
          </Form>
        );
      }}
    </Formik>
  );
};
